// One pin's thread: the note, the screenshot, replies and status changes, a reply box and the status
// moves this viewer may make.
import { ExternalLink, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { ApiError } from "../../lib/api";
import { FEEDBACK_UI_ATTR } from "../../lib/feedback/anchor";
import { pinHref } from "../../lib/feedback/context";
import { screenshotUrl, STATUS_LABEL, useFeedbackActions, useFeedbackPin, type PinDetail, type PinStatus } from "../../lib/feedback/api";
import { roleLabel } from "../../lib/roles";
import { cx, Spinner } from "../ui";
import { FbButton, StatusChip, when } from "./parts";

const MOVE_LABEL: Record<PinStatus, string> = { open: "Reopen", in_progress: "In progress", fixed: "Mark fixed", closed: "Close" };

function who(name: string, role: string, acting: string | null) {
  return `${name} · ${roleLabel(role)}${acting ? ` acting as ${roleLabel(acting).toLowerCase()}` : ""}`;
}

export function ThreadBody({ pin, showOpenLink }: { pin: PinDetail; showOpenLink?: boolean }) {
  const { reply, setStatus } = useFeedbackActions();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (key: string, f: () => Promise<unknown>) => {
    setBusy(key);
    setError(null);
    try {
      await f();
      if (key === "reply") setText("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-[15px] font-semibold text-ink">Pin {pin.number}</span>
        <StatusChip status={pin.status} />
        {showOpenLink ? (
          <Link to={pinHref(pin)} className="focus-ring ml-auto inline-flex h-11 items-center gap-1 rounded-md px-2 text-[13px] font-medium text-link hover:underline">
            Open on page <ExternalLink size={13} aria-hidden />
          </Link>
        ) : null}
      </div>
      <div>
        <div className="text-[12px] text-ink-3">
          {who(pin.author_name, pin.author_role, pin.acting_role)} · {when(pin.created_at)}
        </div>
        <p className="mt-1 text-[14px] leading-5 whitespace-pre-wrap text-ink">{pin.note}</p>
      </div>
      {pin.screenshot ? (
        <a href={screenshotUrl(pin.id)} target="_blank" rel="noreferrer" className="focus-ring block overflow-hidden rounded-md border border-subtle bg-sunken">
          <img src={screenshotUrl(pin.id)} alt={`Screenshot for pin ${pin.number}`} className="max-h-48 w-full object-contain" loading="lazy" />
        </a>
      ) : null}
      <div className="text-[12px] text-ink-3">
        {pin.route}
        {pin.query ? `?${pin.query}` : ""} · {pin.viewport_width}×{pin.viewport_height} · web {pin.build_sha.slice(0, 12)} · api {pin.api_build_sha.slice(0, 12)}
      </div>
      {pin.entries.length ? (
        <ol className="space-y-2 border-t border-subtle pt-3">
          {pin.earlier_entries > 0 ? (
            <li className="text-[12px] text-ink-3">
              {pin.earlier_entries} earlier {pin.earlier_entries === 1 ? "entry" : "entries"} not shown
            </li>
          ) : null}
          {pin.entries.map((e) => (
            <li key={e.id} className="text-[13px]">
              <div className="text-[12px] text-ink-3">
                {who(e.author_name, e.author_role, e.acting_role)} · {when(e.created_at)}
              </div>
              {e.kind === "reply" ? (
                <p className="mt-0.5 whitespace-pre-wrap text-ink">{e.body}</p>
              ) : (
                <p className="mt-0.5 text-ink-2">
                  Moved from {STATUS_LABEL[e.status_from!]} to {STATUS_LABEL[e.status_to!]}
                </p>
              )}
            </li>
          ))}
        </ol>
      ) : null}
      {pin.can_reply ? (
        <form
          className="space-y-2 border-t border-subtle pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) void run("reply", () => reply(pin.id, text.trim()));
          }}
        >
          <label htmlFor={`reply-${pin.id}`} className="sr-only">
            Reply
          </label>
          <textarea
            id={`reply-${pin.id}`}
            value={text}
            rows={2}
            onChange={(e) => setText(e.target.value)}
            placeholder="Reply…"
            className="focus-ring block w-full resize-y rounded-md border border-input bg-surface px-2.5 py-2 text-[14px] text-ink"
          />
          <div className="flex flex-wrap items-center gap-2">
            {pin.next_statuses.map((s) => (
              <FbButton key={s} disabled={!!busy} onClick={() => void run(s, () => setStatus(pin.id, s))}>
                {busy === s ? <Spinner label="Saving" /> : null} {MOVE_LABEL[s]}
              </FbButton>
            ))}
            <span className="flex-1" />
            <FbButton type="submit" variant="primary" disabled={!text.trim() || !!busy}>
              {busy === "reply" ? <Spinner label="Sending" /> : null} Reply
            </FbButton>
          </div>
        </form>
      ) : null}
      {error ? (
        <p role="alert" className="text-[12px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The thread as a side panel (a bottom sheet on phones). */
export function PinThreadPanel({ id, onClose, showOpenLink }: { id: string; onClose: () => void; showOpenLink?: boolean }) {
  const q = useFeedbackPin(id);
  return (
    <aside
      {...{ [FEEDBACK_UI_ATTR]: "" }}
      aria-label="Feedback thread"
      className={cx(
        "pointer-events-auto fixed z-[80] flex flex-col border border-line bg-raised text-ink shadow-[var(--shadow-2)]",
        "inset-x-0 bottom-0 max-h-[80vh] rounded-t-[12px] sm:inset-x-auto sm:top-[72px] sm:right-3 sm:bottom-auto sm:max-h-[calc(100vh-96px)] sm:w-[380px] sm:rounded-[10px]",
      )}
    >
      <div className="flex items-center justify-between border-b border-subtle px-3 py-1">
        <h2 className="eyebrow text-ink-2">Feedback</h2>
        <button type="button" onClick={onClose} aria-label="Close thread" className="focus-ring inline-flex h-11 w-11 items-center justify-center rounded-md text-ink-2 hover:bg-hover">
          <X size={18} aria-hidden />
        </button>
      </div>
      <div className="min-h-0 overflow-y-auto p-3">
        {q.isLoading ? (
          <div className="flex items-center gap-2 text-ink-2">
            <Spinner /> Loading…
          </div>
        ) : q.error ? (
          <p className="text-[13px] text-ink-2">{q.error instanceof ApiError && q.error.status === 404 ? "This pin isn't available to you." : (q.error as Error).message}</p>
        ) : q.data ? (
          <ThreadBody key={q.data.id} pin={q.data} showOpenLink={showOpenLink} />
        ) : null}
      </div>
    </aside>
  );
}
